import { ReactNode } from 'react';
import { SharedSearchBar } from './shared-search-bar';
import SharedButton from './buttons/shared-button';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import CircularProgress from '@mui/material/CircularProgress';
import ListItemButton from '@mui/material/ListItemButton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { alpha, useTheme } from '@mui/material/styles';
import ArrowForward from '@mui/icons-material/ArrowForward';
import DeleteOutlined from '@mui/icons-material/DeleteOutlined';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import ListOutlined from '@mui/icons-material/ListOutlined';

const SOURCE_TYPE_NOUNS: Record<string, string> = {
  User: 'users',
  All_Users: 'users',
  Group: 'groups',
  All_Groups: 'groups',
  OrgUnit: 'org units',
  Domain: 'domains',
  gPanel_Role: 'roles',
  Resource_Calendar: 'resource calendars',
  Shared_Drive: 'shared drives',
};

const getSourceTypeNoun = (sourceType: string) =>
  SOURCE_TYPE_NOUNS[sourceType] ?? 'items';

const capitalize = (value: string) =>
  value.charAt(0).toUpperCase() + value.slice(1);

export function InlineInfoMessage({
  children,
  variant = 'body2',
}: {
  children: ReactNode;
  variant?: 'body1' | 'body2';
}) {
  const theme = useTheme();
  const color = theme.palette.semantic.metricText.info;
  return (
    <Stack direction="row" alignItems="center" gap={0.75} sx={{ py: 0.5 }}>
      <InfoOutlined sx={{ color }} />
      <Typography variant={variant} sx={{ color }}>
        {children}
      </Typography>
    </Stack>
  );
}

interface SharedTransferListNewProps {
  sourceType: string;
  leftItems: readonly string[];
  rightItems: readonly string[];
  rightTotal: number;
  checked: readonly string[];
  getLabel: (value: string) => ReactNode;
  leftSearch: string;
  rightSearch: string;
  onSearch: (value: string, isLeftList: boolean) => void;
  onToggle: (value: string) => () => void;
  onToggleAllLeft: () => void;
  onToggleAllRight: () => void;
  leftSelectedCount: number;
  rightSelectedCount: number;
  leftLoading: boolean;
  leftListFooter?: ReactNode;
  onAddSelected: () => void;
  addSelectedDisabled: boolean;
  onRemove: () => void;
  removeDisabled: boolean;
  showIncludeAll: boolean;
  allSource?: boolean;
  includeAllDisabled: boolean;
  onIncludeAllChange: (checked: boolean) => void;
  errors?: boolean;
  helperText?: string;
}

export default function SharedTransferListNew({
  sourceType,
  leftItems,
  rightItems,
  rightTotal,
  checked,
  getLabel,
  leftSearch,
  rightSearch,
  onSearch,
  onToggle,
  onToggleAllLeft,
  onToggleAllRight,
  leftSelectedCount,
  rightSelectedCount,
  leftLoading,
  leftListFooter,
  onAddSelected,
  addSelectedDisabled,
  onRemove,
  removeDisabled,
  showIncludeAll,
  allSource = false,
  includeAllDisabled,
  onIncludeAllChange,
  errors,
  helperText,
}: SharedTransferListNewProps) {
  const theme = useTheme();
  const noun = getSourceTypeNoun(sourceType);
  const semantic = theme.palette.semantic;
  const panelBorder = `0.5px solid ${semantic.outline.light}`;

  const styles = {
    panel: {
      display: 'flex',
      flexDirection: 'column',
      minHeight: 0,
      height: '100%',
      borderRadius: 2,
      border: panelBorder,
      overflow: 'hidden',
    },
    toolbar: {
      p: 2,
      gap: 1.5,
      borderBottom: panelBorder,
    },
    title: {
      fontWeight: 600,
      color: semantic.text.title2,
    },
    selectedCount: {
      fontWeight: 500,
      color: semantic.text.body,
    },
    list: {
      flex: 1,
      minHeight: 0,
      overflowY: 'auto',
      px: 2,
      py: 1,
    },
    row: {
      height: 48,
      gap: 2,
      p: 0,
      // The disabled checkbox already shows the state;
      '&.Mui-disabled': { opacity: 1 },
    },
    illustration: {
      width: 60,
      height: 60,
      borderRadius: '50%',
      backgroundColor: semantic.surface.bg3,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
  };

  const countChecked = (items: readonly string[]) =>
    items.filter((item) => checked.includes(item)).length;

  const headerCheckbox = (
    items: readonly string[],
    onClick: () => void,
    disabled: boolean
  ) => {
    const count = countChecked(items);
    return (
      <Checkbox
        sx={{ p: 0 }}
        onClick={onClick}
        checked={count === items.length && items.length !== 0}
        indeterminate={count !== items.length && count !== 0}
        disabled={disabled}
        inputProps={{ 'aria-label': 'all items selected' }}
      />
    );
  };

  const itemRows = (items: readonly string[], disabled: boolean) =>
    items.map((value) => {
      const labelId = `transfer-list-new-item-${value}-label`;
      return (
        // ListItemButton keeps the row focusable and toggles on Enter/Space.
        <ListItemButton
          key={value}
          role="listitem"
          disabled={disabled}
          onClick={onToggle(value)}
          sx={styles.row}
        >
          <Checkbox
            sx={{ p: 0 }}
            checked={checked.includes(value)}
            disabled={disabled}
            tabIndex={-1}
            disableRipple
            inputProps={{ 'aria-labelledby': labelId }}
          />
          <Typography
            id={labelId}
            variant="body2"
            noWrap
            sx={{ color: semantic.text.body, flex: 1, minWidth: 0 }}
          >
            {getLabel(value)}
          </Typography>
        </ListItemButton>
      );
    });

  const rightEmptyState = (title: string, message: string) => (
    <Stack
      alignItems="center"
      justifyContent="center"
      gap={3}
      sx={{ flex: 1, px: 3, textAlign: 'center' }}
    >
      <Box sx={styles.illustration}>
        <ListOutlined sx={{ fontSize: 33, color: semantic.icon.default }} />
      </Box>
      <Stack>
        <Typography variant="body2" sx={{ color: semantic.text.support }}>
          {title}
        </Typography>
        <Typography variant="caption" sx={{ color: semantic.text.disabled }}>
          {message}
        </Typography>
      </Stack>
    </Stack>
  );

  return (
    <Stack gap={1} sx={{ height: '100%', minHeight: 0, width: '100%' }}>
      {showIncludeAll && (
        <Stack gap={1.25} sx={{ pb: 0.75 }}>
          <Stack
            component="label"
            direction="row"
            alignItems="center"
            gap={1.5}
            sx={{
              p: 1,
              borderRadius: 1,
              backgroundColor: semantic.surface.bg3,
              cursor: includeAllDisabled ? 'default' : 'pointer',
            }}
          >
            <Checkbox
              sx={{ p: 0 }}
              checked={allSource}
              disabled={includeAllDisabled}
              onChange={(event) => onIncludeAllChange(event.target.checked)}
            />
            <Typography variant="body2" sx={{ color: semantic.text.body }}>
              <Box component="span" sx={{ fontWeight: 500, mr: 0.75 }}>
                Include ALL {noun}?
              </Box>
              This applies to every {noun} in your domain, including {noun}{' '}
              created after this bulk operation is set up.
            </Typography>
          </Stack>
          {allSource && (
            <InlineInfoMessage>
              The list is turned off because all {noun} are included.
            </InlineInfoMessage>
          )}
        </Stack>
      )}
      <Stack
        direction="row"
        gap={2}
        sx={{ flex: 1, minHeight: 360, width: '100%' }}
      >
        {/* Available (left) panel */}
        <Box
          sx={{
            ...styles.panel,
            position: 'relative',
            width: 480,
            flexShrink: 0,
            backgroundColor: semantic.surface.bg2,
          }}
        >
          <Stack sx={styles.toolbar}>
            <Typography variant="body1" sx={styles.title}>
              Available {noun}
            </Typography>
            <SharedSearchBar
              fullWidth
              placeholder="Search..."
              value={leftSearch}
              onChange={(e) => onSearch(e.target.value, true)}
              disabled={allSource}
              sx={{ width: '100%' }}
            />
            <Stack direction="row" alignItems="center" gap={1}>
              {headerCheckbox(
                leftItems,
                onToggleAllLeft,
                leftItems.length === 0 || allSource
              )}
              <Typography variant="body2" sx={styles.selectedCount}>
                {leftSelectedCount} Selected
              </Typography>
            </Stack>
          </Stack>
          {leftLoading ? (
            <Stack alignItems="center" justifyContent="center" sx={{ p: 2 }}>
              <CircularProgress />
            </Stack>
          ) : (
            <Box role="list" sx={styles.list}>
              {leftItems.length === 0 ? (
                <Typography
                  variant="body2"
                  align="center"
                  sx={{ mt: 2, color: semantic.text.support }}
                >
                  No results found
                </Typography>
              ) : (
                itemRows(leftItems, allSource)
              )}
              {leftListFooter}
            </Box>
          )}
          <Box sx={{ p: 2, borderTop: panelBorder }}>
            <SharedButton
              fullWidth
              variant="primary"
              onClick={onAddSelected}
              disabled={addSelectedDisabled || allSource}
              endIcon={<ArrowForward sx={{ fontSize: 16 }} />}
            >
              Add Selected
            </SharedButton>
          </Box>
          {allSource && (
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                backgroundColor: alpha(semantic.surface.bg2, 0.5),
              }}
            />
          )}
        </Box>

        {/* Added (right) panel */}
        <Box
          sx={{
            ...styles.panel,
            flex: 1,
            minWidth: 0,
            backgroundColor: semantic.surface.bg1,
            ...(errors &&
              !allSource && { borderColor: theme.palette.error.main }),
          }}
        >
          {allSource ? (
            rightEmptyState(
              `All ${noun} are included`,
              `Including ${noun} created later`
            )
          ) : rightTotal === 0 ? (
            rightEmptyState(
              `No ${noun} added yet`,
              `Select ${noun} on the left to get started`
            )
          ) : (
            <>
              <Stack sx={styles.toolbar}>
                <Typography variant="body1" sx={styles.title}>
                  {capitalize(noun)} added ({rightTotal})
                </Typography>
                <SharedSearchBar
                  fullWidth
                  placeholder="Search..."
                  value={rightSearch}
                  onChange={(e) => onSearch(e.target.value, false)}
                  sx={{ width: '100%' }}
                />
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <Stack direction="row" alignItems="center" gap={1}>
                    {headerCheckbox(
                      rightItems,
                      onToggleAllRight,
                      rightItems.length === 0
                    )}
                    <Typography variant="body2" sx={styles.selectedCount}>
                      {rightSelectedCount} Selected
                    </Typography>
                  </Stack>
                  <Button
                    variant="text"
                    size="small"
                    onClick={onRemove}
                    disabled={removeDisabled}
                    startIcon={<DeleteOutlined />}
                    sx={{
                      textTransform: 'none',
                      fontWeight: 500,
                      color: semantic.text.body,
                    }}
                  >
                    Remove
                  </Button>
                </Stack>
              </Stack>
              <Box role="list" sx={styles.list}>
                {rightItems.length === 0 ? (
                  <Typography
                    variant="body2"
                    align="center"
                    sx={{ mt: 2, color: semantic.text.support }}
                  >
                    No results found
                  </Typography>
                ) : (
                  itemRows(rightItems, false)
                )}
              </Box>
            </>
          )}
          {errors && !allSource && helperText && (
            <Typography color="error" variant="caption" sx={{ px: 2, pb: 1 }}>
              {helperText}
            </Typography>
          )}
        </Box>
      </Stack>
    </Stack>
  );
}
